import { contactApiUrl, getApiBase, volunteersApiUrl } from "./apiConfig";
import { adminAuthedFetch } from "./adminAuthedFetch";
import { getAdminJwt } from "./authToken";

const LOCAL_CONTACT_KEY = "vii_site_contact_submissions_v1";
const LOCAL_VOLUNTEER_KEY = "vii_site_volunteer_submissions_v1";

export class FormSubmissionError extends Error {
  constructor(message, { status } = {}) {
    super(message);
    this.name = "FormSubmissionError";
    this.status = status;
  }
}

function safeParseJson(text) {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
}

function readLocalList(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function prependLocal(key, row) {
  try {
    const list = readLocalList(key);
    const next = [row, ...list];
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function newLocalId(prefix) {
  try {
    return `${prefix}_${crypto.randomUUID()}`;
  } catch {
    return `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }
}

function extractArray(data) {
  if (Array.isArray(data)) return data;
  if (!data || typeof data !== "object") return [];
  return (
    data.messages ||
    data.submissions ||
    data.items ||
    data.data ||
    data.applications ||
    data.volunteers ||
    []
  );
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
  } catch {
    return String(iso);
  }
}

export function normalizeContactRow(raw) {
  const id =
    raw._id != null ? String(raw._id) : raw.id != null ? String(raw.id) : newLocalId("c");
  return {
    id,
    name: String(raw.name || "").trim() || "—",
    email: String(raw.email || "").trim() || "—",
    message: String(raw.message || raw.body || raw.content || "").trim() || "—",
    createdAt: raw.createdAt || raw.created_at || raw.submittedAt || "",
    _displayDate: formatWhen(raw.createdAt || raw.created_at || raw.submittedAt),
  };
}

export function normalizeVolunteerRow(raw) {
  const id =
    raw._id != null ? String(raw._id) : raw.id != null ? String(raw.id) : newLocalId("v");
  return {
    id,
    name: String(raw.name || "").trim() || "—",
    phone: String(raw.phone || raw.phoneNumber || raw["phone number"] || "").trim() || "—",
    email: String(raw.email || "").trim() || "—",
    country: String(
      raw.country || raw.countryOfResidence || raw["country of residence"] || ""
    ).trim() || "—",
    occupation: String(raw.occupation || "").trim() || "—",
    areaOfInterest: String(
      raw.areaOfInterest || raw.area_of_interest || raw["Area of Interest"] || ""
    ).trim() || "—",
    additionalInfo: String(
      raw.additionalInfo ||
        raw.additional_information ||
        raw["Additional Information"] ||
        ""
    ).trim() || "—",
    createdAt: raw.createdAt || raw.created_at || raw.submittedAt || "",
    _displayDate: formatWhen(raw.createdAt || raw.created_at || raw.submittedAt),
  };
}

export async function submitContactMessage({ name, email, message }) {
  const payload = {
    name: String(name || "").trim(),
    email: String(email || "").trim(),
    message: String(message || "").trim(),
  };
  if (!payload.name || !payload.email || !payload.message) {
    throw new FormSubmissionError("Please fill in all fields.");
  }

  const base = getApiBase();
  if (!base) {
    const row = normalizeContactRow({
      ...payload,
      id: newLocalId("c"),
      createdAt: new Date().toISOString(),
    });
    prependLocal(LOCAL_CONTACT_KEY, row);
    return { ok: true, localOnly: true };
  }

  const url = contactApiUrl("");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  const body = safeParseJson(await res.text());
  if (!res.ok) {
    const msg =
      typeof body.message === "string"
        ? body.message
        : typeof body.error === "string"
          ? body.error
          : `Could not send message (${res.status})`;
    throw new FormSubmissionError(msg, { status: res.status });
  }

  const row = normalizeContactRow({
    ...payload,
    _id: body._id || body.id,
    createdAt: body.createdAt || new Date().toISOString(),
  });
  prependLocal(LOCAL_CONTACT_KEY, row);

  return { ok: true };
}

export async function submitVolunteerApplication(payload) {
  const bodyOut = {
    name: String(payload.name || "").trim(),
    phone: String(payload.phone || "").trim(),
    email: String(payload.email || "").trim(),
    country: String(payload.country || "").trim(),
    occupation: String(payload.occupation || "").trim(),
    areaOfInterest: String(payload.areaOfInterest || "").trim(),
    additionalInfo: String(payload.additionalInfo || "").trim(),
  };
  if (!bodyOut.name || !bodyOut.phone || !bodyOut.email || !bodyOut.areaOfInterest) {
    throw new FormSubmissionError("Please fill in all required fields.");
  }

  const base = getApiBase();
  if (!base) {
    const row = normalizeVolunteerRow({
      ...bodyOut,
      id: newLocalId("v"),
      createdAt: new Date().toISOString(),
    });
    prependLocal(LOCAL_VOLUNTEER_KEY, row);
    return { ok: true, localOnly: true };
  }

  const url = volunteersApiUrl("");
  const res = await fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(bodyOut),
  });
  const body = safeParseJson(await res.text());
  if (!res.ok) {
    const msg =
      typeof body.message === "string"
        ? body.message
        : typeof body.error === "string"
          ? body.error
          : `Could not submit application (${res.status})`;
    throw new FormSubmissionError(msg, { status: res.status });
  }

  const row = normalizeVolunteerRow({
    ...bodyOut,
    _id: body._id || body.id,
    createdAt: body.createdAt || new Date().toISOString(),
  });
  prependLocal(LOCAL_VOLUNTEER_KEY, row);

  return { ok: true };
}

async function fetchAdminJson(url) {
  const token = getAdminJwt();
  if (!token) return { ok: false, rows: [] };
  const res = await adminAuthedFetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const text = await res.text();
  const data = safeParseJson(text);
  if (!res.ok) return { ok: false, rows: [] };
  return { ok: true, data };
}

export async function fetchAdminContactMessages() {
  const base = getApiBase();
  const local = readLocalList(LOCAL_CONTACT_KEY).map((r) => normalizeContactRow(r));

  if (!base) {
    return local.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }

  const url = contactApiUrl("admin/list");
  if (!url) return local;

  try {
    const { ok, data } = await fetchAdminJson(url);
    if (ok && Array.isArray(data)) {
      return data
        .map((r) => normalizeContactRow(r))
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }
    if (ok) {
      const remote = extractArray(data).map((r) => normalizeContactRow(r));
      if (remote.length > 0) {
        const seen = new Set(remote.map((r) => r.id));
        const merged = [...remote, ...local.filter((l) => !seen.has(l.id))];
        return merged.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      }
    }
  } catch {
    // fall through
  }

  return local.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}

export async function fetchAdminVolunteerApplications() {
  const base = getApiBase();
  const local = readLocalList(LOCAL_VOLUNTEER_KEY).map((r) => normalizeVolunteerRow(r));

  if (!base) {
    return local.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
  }

  const url = volunteersApiUrl("admin/list");
  if (!url) return local;

  try {
    const { ok, data } = await fetchAdminJson(url);
    if (ok && Array.isArray(data)) {
      return data
        .map((r) => normalizeVolunteerRow(r))
        .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    }
    if (ok) {
      const remote = extractArray(data).map((r) => normalizeVolunteerRow(r));
      if (remote.length > 0) {
        const seen = new Set(remote.map((r) => r.id));
        const merged = [...remote, ...local.filter((l) => !seen.has(l.id))];
        return merged.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
      }
    }
  } catch {
    // fall through
  }

  return local.sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
}
